import { Color3 } from "@babylonjs/core/Maths/math.color";
import { CreateLineSystem } from "@babylonjs/core/Meshes/Builders/linesBuilder";
import { CreateBox } from "@babylonjs/core/Meshes/Builders/boxBuilder";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { GeoUtils } from './GeoUtils';
import { OpeningGeometry } from './OpeningGeometry';
import { Util } from '../../utils/Util';
import { NumberProperty, PropertyValue, LengthProperty, PropertyChangedResult, BoolProperty } from '@egr/wcf/modules/core/prop';
export class GlassFront extends OpeningGeometry {
    constructor(pRoomManager, pOpening) {
        super(pRoomManager, pOpening);
        this.mOpening = pOpening;
        if (this.mOpening.OpeningParams !== null) {
            this.mOpening.OpeningParams.privacyFilter = false;
            this.mOpening.OpeningParams.blindVisible = false;
            this.mOpening.OpeningParams.blindDepth = 0.07;
            this.mOpening.OpeningParams.blindOpeningAngle = 60;
            this.mOpening.OpeningParams.blindOpeningWidth = 0;
            this.mOpening.OpeningParams.ColumnCount = 2;
            this.mOpening.OpeningParams.ColumnWidth = 0.75;
            this.mOpening.OpeningParams.minWidth = 0.4;
            this.mOpening.OpeningParams.frameOffset = 0;
        }
    }
    getType() {
        return ("GlassFront");
    }
    get canMirrorV() {
        return (false);
    }
    getInitialParams() {
        let tParams = {};
        tParams.width = 1.5;
        tParams.height = 2.0;
        tParams.floorOffset = 0.0;
        return (tParams);
    }
    create2dGeometry(pParent) {
        let tHW = this.mOpening.width;
        let tFrameW = 0.03;
        let tSW = (tHW - tFrameW) * (1 / this.mOpening.OpeningParams.ColumnCount); // segment width
        let tFrameHD = 0.035;
        let tLines = [];
        //outer frame (left + right)
        tLines.push([
            //left
            new Vector3(-tHW * 0.5, 0.0, -tFrameHD),
            new Vector3(-tHW * 0.5, 0.0, tFrameHD),
            new Vector3(-tHW * 0.5 + tFrameW, 0.0, tFrameHD),
            new Vector3(-tHW * 0.5 + tFrameW, 0.0, -tFrameHD),
            new Vector3(-tHW * 0.5, 0.0, -tFrameHD)
        ]);
        tLines.push([
            //right
            new Vector3(tHW * 0.5, 0.0, -tFrameHD),
            new Vector3(tHW * 0.5, 0.0, tFrameHD),
            new Vector3(tHW * 0.5 - tFrameW, 0.0, tFrameHD),
            new Vector3(tHW * 0.5 - tFrameW, 0.0, -tFrameHD),
            new Vector3(tHW * 0.5, 0.0, -tFrameHD)
        ]);
        for (let i = 1; i <= this.mOpening.OpeningParams.ColumnCount; i++) {
            let tSP = -(tHW * 0.5) + (tSW * (i - 1));
            if ((this.mOpening.OpeningParams.ColumnCount >= 2) && (i >= 2)) {
                tSP = (-((tHW - tFrameW) * 0.5) + (tSW * (i - 1))) - (tFrameW * 0.5);
                //inner vertical bars
                if (i > 1) {
                    tLines.push([
                        new Vector3(tSP, 0.0, -tFrameHD),
                        new Vector3(tSP, 0.0, tFrameHD),
                        new Vector3(tSP + tFrameW, 0.0, tFrameHD),
                        new Vector3(tSP + tFrameW, 0.0, -tFrameHD),
                        new Vector3(tSP, 0.0, -tFrameHD)
                    ]);
                }
            }
            //window pane
            tLines.push([
                new Vector3(tSP + tFrameW, 0.0, 0.0),
                new Vector3(tSP + tSW, 0.0, 0.0)
            ]);
        }
        let tLines2D = CreateLineSystem("", {
            lines: tLines,
            updatable: false,
            instance: null
        }, this.mScene);
        tLines2D.isPickable = false;
        tLines2D.intersectionThreshold = 0;
        tLines2D.color = Color3.Black();
        tLines2D.position.y = 0.0005;
        tLines2D.parent = pParent;
        this.prepare2dMesh(tLines2D);
    }
    create3dGeometry(pParent) {
        this.createFrame(pParent);
        this.createBlind(pParent, this.mOpening.OpeningParams);
    }
    getProperties() {
        let tProperties = new Array();
        //front segment number
        let tSegmentsProp = new NumberProperty(this.mOpening, GlassFront.PROP_KEY_WINDOWSEGMENTS, this.mRoomManager.resources.propertySegments);
        tSegmentsProp.minValue = 1;
        tSegmentsProp.maxValue = 10;
        tSegmentsProp.numDecDigits = 0;
        tSegmentsProp.numDigits = 2;
        tSegmentsProp.getValue = () => new PropertyValue(this.mOpening.OpeningParams.ColumnCount);
        tSegmentsProp.setValue = (pValue) => {
            let tValue = LengthProperty.ConvertValueToNumber(pValue);
            if (tValue != null && this.setSegments(tValue, this.mOpening.OpeningParams))
                return (Promise.resolve(PropertyChangedResult.Value));
            return (Promise.resolve(PropertyChangedResult.Nothing));
        };
        tProperties.push(tSegmentsProp);
        //front segment width
        let tSegmentsWidthProp = new LengthProperty(this.mOpening, GlassFront.PROP_KEY_WINDOWSEGMENTSWIDTH, this.mRoomManager.resources.propertyColumnWidth);
        tSegmentsWidthProp.minValue = 0.1;
        tSegmentsWidthProp.maxValue = 50;
        tSegmentsWidthProp.numDecDigits = 0;
        tSegmentsWidthProp.numDigits = 2;
        tSegmentsWidthProp.getValue = () => new PropertyValue(this.mOpening.OpeningParams.ColumnWidth);
        tSegmentsWidthProp.setValue = (pValue) => {
            let tValue = LengthProperty.ConvertValueToNumber(pValue);
            if (tValue != null && this.setSegmentsWidth(tValue, this.mOpening.OpeningParams))
                return (Promise.resolve(PropertyChangedResult.Value));
            return (Promise.resolve(PropertyChangedResult.Nothing));
        };
        tProperties.push(tSegmentsWidthProp);
        let tYesNoChoices = new Array();
        for (let tYesNo of this.mRoomManager._YesNo)
            tYesNoChoices.push(this.mRoomManager._getYesNoPropValue(tYesNo));
        //set blind
        let tBlindProp = new BoolProperty(this.mOpening, GlassFront.PROP_KEY_BLIND, this.mRoomManager.resources.propertyBlind);
        tBlindProp.choiceList = true;
        tBlindProp.getChoices = () => Promise.resolve(tYesNoChoices);
        tBlindProp.getValue = () => this.mRoomManager._getYesNoPropValue(this.mOpening.OpeningParams.blindVisible);
        tBlindProp.setValue = (pValue) => {
            if (pValue != null) {
                let tStr = BoolProperty.ConvertValueToBool(pValue);
                this.setBlind(tStr, this.mOpening.OpeningParams);
                return (Promise.resolve(PropertyChangedResult.Value));
            }
            return (Promise.resolve(PropertyChangedResult.Nothing));
        };
        tProperties.push(tBlindProp);
        //blind opening angle
        let tBlindOpeningAngleProp = new NumberProperty(this.mOpening, GlassFront.PROP_KEY_BLINDOPENINGANGLE, this.mRoomManager.resources.propertyOpeningAngle);
        tBlindOpeningAngleProp.minValue = -90;
        tBlindOpeningAngleProp.maxValue = 90;
        tBlindOpeningAngleProp.numDecDigits = 0;
        tBlindOpeningAngleProp.numDigits = 2;
        tBlindOpeningAngleProp.visible = this.mOpening.OpeningParams.blindVisible;
        tBlindOpeningAngleProp.getValue = () => new PropertyValue(this.mOpening.OpeningParams.blindOpeningAngle);
        tBlindOpeningAngleProp.setValue = (pValue) => {
            let tValue = LengthProperty.ConvertValueToNumber(pValue);
            if (tValue != null && this.setBlindOpeningAngle(tValue, this.mOpening.OpeningParams))
                return (Promise.resolve(PropertyChangedResult.Value));
            return (Promise.resolve(PropertyChangedResult.Nothing));
        };
        tProperties.push(tBlindOpeningAngleProp);
        //blind opening width
        let tBlindOpeningWidthProp = new NumberProperty(this.mOpening, GlassFront.PROP_KEY_BLINDOPENINGWIDTH, this.mRoomManager.resources.propertyOpeningWidth);
        tBlindOpeningWidthProp.minValue = 0;
        tBlindOpeningWidthProp.maxValue = this.mOpening.width;
        tBlindOpeningWidthProp.numDecDigits = 1;
        tBlindOpeningWidthProp.numDigits = 3;
        tBlindOpeningWidthProp.visible = this.mOpening.OpeningParams.blindVisible;
        tBlindOpeningWidthProp.getValue = () => new PropertyValue(this.mOpening.OpeningParams.blindOpeningWidth);
        tBlindOpeningWidthProp.setValue = (pValue) => {
            let tValue = LengthProperty.ConvertValueToNumber(pValue);
            if (tValue != null && this.setBlindOpeningWidth(tValue, this.mOpening.OpeningParams))
                return (Promise.resolve(PropertyChangedResult.Value));
            return (Promise.resolve(PropertyChangedResult.Nothing));
        };
        tProperties.push(tBlindOpeningWidthProp);
        //set privacy filter
        let tPrivacyFilterProp = new BoolProperty(this.mOpening, GlassFront.PROP_KEY_PRIVACYFILTER, this.mRoomManager.resources.propertyPrivacyFilter);
        tPrivacyFilterProp.choiceList = true;
        tPrivacyFilterProp.getChoices = () => Promise.resolve(tYesNoChoices);
        tPrivacyFilterProp.getValue = () => this.mRoomManager._getYesNoPropValue(this.mOpening.OpeningParams.privacyFilter);
        tPrivacyFilterProp.setValue = (pValue) => {
            if (pValue != null) {
                let tStr = BoolProperty.ConvertValueToBool(pValue);
                this.setPrivacyFilter(tStr, this.mOpening.OpeningParams);
                return (Promise.resolve(PropertyChangedResult.Value));
            }
            return (Promise.resolve(PropertyChangedResult.Nothing));
        };
        tProperties.push(tPrivacyFilterProp);
        return (tProperties);
    }
    serializeIOData() {
        return (this.mOpening.OpeningParams);
    }
    loadIOData(pData) {
        let tEParams = pData;
        let tParams = pData;
        tParams.ColumnCount = tEParams.ColumnCount;
        this.setSegments(tParams.ColumnCount, this.mOpening.OpeningParams);
        this.setBlind(tParams.blindVisible, this.mOpening.OpeningParams);
        this.setBlindOpeningAngle(tParams.blindOpeningAngle, this.mOpening.OpeningParams);
        this.setBlindOpeningWidth(tParams.blindOpeningWidth, this.mOpening.OpeningParams);
        this.setPrivacyFilter(tParams.privacyFilter, this.mOpening.OpeningParams);
    }
    createFrame(pParent) {
        let tOP = this.mOpening.OpeningParams;
        let tHeight = this.mOpening.height;
        let tHW = this.mOpening.width;
        let tSW = (tHW - GlassFront.FRAME_THICKNESS) * (1 / tOP.ColumnCount); // segment width
        let tFrame = GeoUtils.createFrame(this.mOpening.width, tHeight, GlassFront.FRAME_DEPTH, GlassFront.FRAME_THICKNESS, pParent, this.mScene);
        let tCnt = Util.intDIV(tHW, tOP.ColumnWidth);
        if (tHW > tOP.ColumnWidth && tCnt != Infinity) {
            let toffset = ((tHW / tOP.ColumnWidth) - tCnt) * tOP.ColumnWidth * 0.5;
            if (toffset < 0.1) {
                tOP.ColumnWidth = tHW / tCnt;
                toffset = 0;
            }
            tOP.ColumnCount = tCnt;
            let tVerticalBar = CreateBox("", {
                width: GlassFront.FRAME_THICKNESS,
                height: this.mOpening.height - GlassFront.FRAME_THICKNESS * 2,
                depth: GlassFront.FRAME_DEPTH * 0.5
            }, this.mScene);
            tVerticalBar.material = this.getMetalMaterial();
            tVerticalBar.position.x = toffset - tHW * 0.5;
            tVerticalBar.parent = tFrame;
            for (let i = 1; i <= tCnt; i++) {
                let tSP = i * tOP.ColumnWidth;
                let tVerticalBar1 = CreateBox("", {
                    width: GlassFront.FRAME_THICKNESS,
                    height: this.mOpening.height - GlassFront.FRAME_THICKNESS * 2,
                    depth: GlassFront.FRAME_DEPTH * 0.5
                }, this.mScene);
                tVerticalBar1.material = this.getMetalMaterial();
                tVerticalBar1.position.x = toffset + tSP - tHW * 0.5;
                tVerticalBar1.parent = tFrame;
            }
        }
        if (tFrame != null) {
            tFrame.position.y = this.mOpening.height - tHeight * 0.5;
            tFrame.material = this.getMetalMaterial();
            if (this.mOpening.wall != null)
                tFrame.name = "room:wall|" + this.mOpening.wall.id.toString();
            this.prepare3dMesh(tFrame);
        }
        this.createGlassPane(pParent);
        return (tFrame);
    }
    createGlassPane(pParent) {
        var tW = this.mOpening.width;
        var tH = this.mOpening.height;
        var tGlassInlay = CreateBox("", { width: tW, height: tH,
            depth: 0.005
        }, this.mScene);
        tGlassInlay.position.y = tH * 0.5;
        tGlassInlay.position.x = 0;
        tGlassInlay.position.z = 0;
        tGlassInlay.material = this.getSheetGlassMaterial();
        if (this.mOpening.wall != null)
            tGlassInlay.name = "room:wall|" + this.mOpening.wall.id.toString();
        tGlassInlay.parent = pParent;
        this.prepare3dMesh(tGlassInlay);
        if (this.mOpening.OpeningParams.privacyFilter) {
            this.createPrivacyFilter(tGlassInlay, false);
        }
    }
}
//// private ///////////////////////////////////////////////////////////////////////////////
GlassFront.FRAME_DEPTH = 0.1;
GlassFront.FRAME_THICKNESS = 0.03;
